import { Button, Grid, Typography, useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import InternshipCard from '../../components/InternshipCard';

import { Context as AppContext } from "../../context/AppContext";
import { getImageUrl } from '../../utils/functions';
import { MenuBook, Rocket, School, Work, TrackChanges, Groups } from "@mui/icons-material";

const ListOfInterships = ({ internships, interns, disableCard = false }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const {state: {currentUser}} = useContext(AppContext);

    // Augment internships with intern data
    const augmentedInternships = React.useMemo(() => {
        if (!internships) return [];

        return internships.map(internship => {
            const intern = interns?.find(i => i.uid === internship.studentUid);
            return {
                ...internship,
                internData: intern || null
            };
        });
    }, [internships, interns]);

    if (!augmentedInternships || augmentedInternships?.length === 0) {
        const emptyStateContent = (
            <Box sx={{ p: disableCard ? { xs: 2, md: 2 } : { xs: 4, md: 6 }, textAlign: 'center' }}>
                <Box sx={{ mb: disableCard ? 2 : 4 }}>
                    <Typography
                        variant={disableCard ? "h6" : "h4"}
                        sx={{
                            fontWeight: 600,
                            color: '#1e293b',
                            mb: 2,
                            fontSize: disableCard ? { xs: '1rem', md: '1.125rem' } : { xs: '1.5rem', md: '2rem' }
                        }}
                    >
                        No Internships Yet
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: '#64748b',
                            fontSize: disableCard ? '0.875rem' : '1.125rem',
                            maxWidth: disableCard ? '400px' : '500px',
                            mx: 'auto',
                            lineHeight: 1.6
                        }}
                    >
                        Start your mentoring journey by accepting student applications and building meaningful connections.
                    </Typography>
                </Box>

                <Box sx={{ mb: disableCard ? 2 : 4, display: 'flex', justifyContent: 'center' }}>
                    <Box sx={{
                        width: disableCard ? 80 : 120,
                        height: disableCard ? 80 : 120,
                        bgcolor: '#f8fafc',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '2px solid #e2e8f0'
                    }}>
                        <Groups sx={{ fontSize: disableCard ? '2.5rem' : '4rem', color: '#8b5cf6' }} />
                    </Box>
                </Box>

                <Button
                    onClick={() => navigate('/applicants')}
                    variant="contained"
                    size={disableCard ? "medium" : "large"}
                    sx={{
                        bgcolor: theme.palette.primary.main,
                        color: 'white',
                        fontSize: disableCard ? '0.875rem' : '1rem',
                        fontWeight: 600,
                        px: disableCard ? 3 : 4,
                        py: disableCard ? 1 : 1.5,
                        borderRadius: '12px',
                        textTransform: 'none',
                        minWidth: disableCard ? '140px' : '180px',
                        boxShadow: `0 4px 14px 0 ${theme.palette.primary.main}40`,
                        '&:hover': {
                            bgcolor: theme.palette.primary.dark || '#4c2a6b',
                            boxShadow: `0 6px 20px 0 ${theme.palette.primary.main}59`,
                            transform: 'translateY(-1px)'
                        },
                        '&:focus': {
                            outline: `2px solid ${theme.palette.primary.main}`,
                            outlineOffset: '2px'
                        },
                        transition: 'all 0.2s ease-in-out'
                    }}
                >
                    View Applicants
                </Button>

                {!disableCard && (
                    <Box sx={{
                        mt: 4,
                        pt: 3,
                        borderTop: '1px solid #f1f5f9',
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                        gap: 3
                    }}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Box sx={{
                                width: 48,
                                height: 48,
                                bgcolor: '#f0f9ff',
                                borderRadius: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mx: 'auto',
                                mb: 2
                            }}>
                                <School sx={{ fontSize: '1.5rem', color: '#3b82f6' }} />
                            </Box>
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    fontWeight: 600,
                                    color: '#1e293b',
                                    mb: 0.5
                                }}
                            >
                                Accept Students
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ color: '#64748b' }}
                            >
                                Review and accept applications
                            </Typography>
                        </Box>

                        <Box sx={{ textAlign: 'center' }}>
                            <Box sx={{
                                width: 48,
                                height: 48,
                                bgcolor: '#f0fdf4',
                                borderRadius: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mx: 'auto',
                                mb: 2
                            }}>
                                <Work sx={{ fontSize: '1.5rem', color: '#10b981' }} />
                            </Box>
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    fontWeight: 600,
                                    color: '#1e293b',
                                    mb: 0.5
                                }}
                            >
                                Start Mentorships
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ color: '#64748b' }}
                            >
                                Begin the mentoring process
                            </Typography>
                        </Box>

                        <Box sx={{ textAlign: 'center' }}>
                            <Box sx={{
                                width: 48,
                                height: 48,
                                bgcolor: '#fefce8',
                                borderRadius: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mx: 'auto',
                                mb: 2
                            }}>
                                <TrackChanges sx={{ fontSize: '1.5rem', color: '#f59e0b' }} />
                            </Box>
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    fontWeight: 600,
                                    color: '#1e293b',
                                    mb: 0.5
                                }}
                            >
                                Track Progress
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ color: '#64748b' }}
                            >
                                Monitor their growth
                            </Typography>
                        </Box>
                    </Box>
                )}
            </Box>
        );

        if (disableCard) {
            return emptyStateContent;
        }

        return (
            <Box sx={{
                bgcolor: 'white',
                borderRadius: '20px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)',
                overflow: 'hidden',
                transition: 'box-shadow 0.2s ease-in-out',
                '&:hover': {
                    boxShadow: '0 10px 25px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
                }
            }}>
                {emptyStateContent}
            </Box>
        );
    }

    const content = (
        <>
            {!disableCard && (
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 3,
                    flexWrap: 'wrap',
                    gap: 2
                }}>
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 600,
                            color: '#1e293b',
                            fontSize: '1.25rem'
                        }}
                    >
                        Active Mentorships
                    </Typography>
                    <Box sx={{
                        px: 2,
                        py: 0.5,
                        bgcolor: '#f0f9ff',
                        color: '#0c4a6e',
                        borderRadius: '20px',
                        fontSize: '0.875rem',
                        fontWeight: 500
                    }}>
                        {augmentedInternships?.length} {augmentedInternships?.length === 1 ? 'Internship' : 'Internships'}
                    </Box>
                </Box>
            )}

            <Grid container spacing={3} justifyContent="flex-start">
                {augmentedInternships?.map((internship, index) => {
                    return (
                        <Grid
                            item
                            xs={12}
                            lg={6}
                            xl={4}
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-start'
                            }}
                            key={internship.uid}
                        >
                            <Box sx={{
                                width: '100%'
                            }}>
                                <InternshipCard
                                    internship={internship}
                                    intern={internship.internData}
                                    index={index}
                                    companyImage={getImageUrl(currentUser.company)}
                                />
                            </Box>
                        </Grid>
                    )
                })}
            </Grid>
        </>
    );

    if (disableCard) {
        return <Box>{content}</Box>;
    }

    return (
        <Box sx={{
            bgcolor: 'white',
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)',
            p: { xs: 2, md: 3 },
            transition: 'box-shadow 0.2s ease-in-out',
            '&:hover': {
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
            }
        }}>
            {content}
        </Box>
    );
};

export default ListOfInterships;